<script setup>
import { onMounted, onUnmounted, watch } from "vue"

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, default: null },
  description: { type: String, default: null },
  size: {
    type: String,
    default: "md",
    validator: (v) => ["sm", "md", "lg", "xl", "2xl", "3xl", "4xl"].includes(v),
  },
  closeOnBackdrop: { type: Boolean, default: true },
  closeOnEscape: { type: Boolean, default: true },
})

const emit = defineEmits(["update:open", "close"])

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
  "2xl": "max-w-4xl",
  "3xl": "max-w-6xl",
  "4xl": "max-w-7xl",
}

const close = () => {
  emit("update:open", false)
  emit("close")
}

const onBackdrop = () => {
  if (props.closeOnBackdrop) close()
}

const onKey = (event) => {
  if (event.key === "Escape" && props.closeOnEscape && props.open) {
    close()
  }
}

watch(
  () => props.open,
  (open) => {
    if (typeof document === "undefined") return
    document.body.style.overflow = open ? "hidden" : ""
  },
  { immediate: true },
)

onMounted(() => window.addEventListener("keydown", onKey))
onUnmounted(() => {
  window.removeEventListener("keydown", onKey)
  if (typeof document !== "undefined") {
    document.body.style.overflow = ""
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto px-4 py-6"
        role="dialog"
        aria-modal="true"
      >
        <div class="absolute inset-0 bg-black/40" @click="onBackdrop" />

        <div
          :class="[
            'relative flex max-h-[min(90vh,calc(100vh-3rem))] w-full flex-col bg-white rounded-2xl shadow-xl my-auto',
            sizeClasses[size],
          ]"
        >
          <header
            v-if="title || $slots.header"
            class="shrink-0 px-6 pt-6 pb-3"
          >
            <slot name="header">
              <h2 class="text-lg font-semibold text-black">{{ title }}</h2>
              <p v-if="description" class="text-sm text-black/60 mt-1">
                {{ description }}
              </p>
            </slot>
          </header>

          <div class="min-h-0 flex-1 overflow-y-auto px-6 py-4 text-sm text-black">
            <slot />
          </div>

          <footer
            v-if="$slots.footer"
            class="shrink-0 px-6 py-4 flex items-center justify-end gap-2"
          >
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
